import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { buildTokenDetailPath } from '@/config/tokenProfiles'
import { useTranslation } from '@/features/locale/LocaleProvider'
import { NATIVE_TOKEN_ADDRESS } from '@sirius-cybernetics-42/product-config'
import { useAppWeb3 } from '@/hooks'
import { MobileFullScreenSheet } from '@/mobile/platform'
import type { Currency } from '@/sdk'
import { Token } from '@/sdk'

import { MobileTokenPickerContent } from './MobileTokenPickerContent'

type MobileTokenSearchContextValue = {
  openTokenSearch: () => void
  closeTokenSearch: () => void
}

const MobileTokenSearchContext = createContext<MobileTokenSearchContextValue | null>(null)

export function useMobileTokenSearch() {
  const context = useContext(MobileTokenSearchContext)
  if (!context) {
    throw new Error('useMobileTokenSearch debe usarse dentro de MobileTokenSearchProvider')
  }
  return context
}

/** Sheet global de búsqueda de tokens (home, menú lateral, etc.). */
export function MobileTokenSearchProvider({ children }: { children: React.ReactNode }) {
  const t = useTranslation()
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const { chainId } = useAppWeb3()

  const openTokenSearch = useCallback(() => setOpen(true), [])
  const closeTokenSearch = useCallback(() => setOpen(false), [])

  const handleSelect = useCallback(
    (currency: Currency) => {
      setOpen(false)
      if (currency instanceof Token) {
        navigate(buildTokenDetailPath(currency.address))
        return
      }
      navigate(buildTokenDetailPath(NATIVE_TOKEN_ADDRESS))
    },
    [navigate],
  )

  const value = useMemo(
    () => ({ openTokenSearch, closeTokenSearch }),
    [openTokenSearch, closeTokenSearch],
  )

  return (
    <MobileTokenSearchContext.Provider value={value}>
      {children}
      <MobileFullScreenSheet open={open} onClose={closeTokenSearch}>
        {chainId != null ? (
          <MobileTokenPickerContent
            chainId={chainId}
            mode="list"
            title={t.shell.tokenSearch.title}
            onSelect={handleSelect}
            onBack={closeTokenSearch}
          />
        ) : null}
      </MobileFullScreenSheet>
    </MobileTokenSearchContext.Provider>
  )
}
